import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import UserProfileIcon from '../auth/UserProfileIcon';
import UserNotification from '../common/UserNotification';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';


function Navbar() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLandingPage = location.pathname === '/';
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Refs to store counts from separate listeners
  const userSpecificUnreadRef = useRef(0);
  const globalUnreadStatusRef = useRef(0);
  const globalNotificationDocsRef = useRef([]);
  const globalNotificationStatusMapRef = useRef(new Map());

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      if (isLandingPage) {
        // On landing page, show navbar after scrolling past hero section
        setIsScrolled(scrollPosition > window.innerHeight * 0.8);
      } else {
        // On other pages, add background when scrolled slightly
        setIsScrolled(scrollPosition > 20);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLandingPage]);

  // Real-time listeners for unread count
  useEffect(() => {
    if (!user) {
       console.log('No user, setting unread count to 0 and cleaning up listeners.');
       setUnreadCount(0);
       userSpecificUnreadRef.current = 0;
       globalUnreadStatusRef.current = 0;
       globalNotificationDocsRef.current = [];
       globalNotificationStatusMapRef.current = new Map();
       return;
    }

    console.log('Setting up real-time listeners for unread notifications...');

    const recalcGlobalUnread = () => {
      const unreadGlobal = globalNotificationDocsRef.current.reduce((count, notification) => {
        const status = globalNotificationStatusMapRef.current.get(notification.id);
        return count + ((status?.read || status?.dismissed) ? 0 : 1);
      }, 0);

      globalUnreadStatusRef.current = unreadGlobal;
      setUnreadCount(userSpecificUnreadRef.current + globalUnreadStatusRef.current);
    };

    const userSpecificQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        where('read', '==', false),
        where('dismissed', '==', false)
    );

    const unsubscribeUserSpecific = onSnapshot(userSpecificQuery, (snapshot) => {
        console.log(`Real-time update: ${snapshot.size} unread user-specific notifications.`);
        userSpecificUnreadRef.current = snapshot.size;
        setUnreadCount(userSpecificUnreadRef.current + globalUnreadStatusRef.current);
    }, (error) => {
      console.error('Error in real-time user-specific notification listener:', error);
    });

    const globalNotificationsQuery = query(
         collection(db, 'notifications'),
         where('userId', '==', 'all')
    );

    const unsubscribeGlobalNotifications = onSnapshot(globalNotificationsQuery, (globalSnapshot) => {
         console.log(`Real-time update: ${globalSnapshot.docs.length} total global notifications.`);
         globalNotificationDocsRef.current = globalSnapshot.docs.map(doc => ({ id: doc.id }));
         recalcGlobalUnread();
    }, (error) => {
       console.error('Error in real-time global notification listener:', error);
    });

    const globalStatusQuery = query(
      collection(db, 'users', user.uid, 'notificationStatus')
    );

    const unsubscribeGlobalStatus = onSnapshot(globalStatusQuery, (statusSnapshot) => {
      const statusMap = new Map();
      statusSnapshot.docs.forEach((statusDoc) => {
        statusMap.set(statusDoc.id, statusDoc.data());
      });
      globalNotificationStatusMapRef.current = statusMap;
      recalcGlobalUnread();
    }, (error) => {
      console.error('Error in real-time notification status listener:', error);
    });

    return () => {
       console.log('Cleaning up real-time listeners.');
       unsubscribeUserSpecific();
       unsubscribeGlobalNotifications();
       unsubscribeGlobalStatus();
    }
  }, [user]); // Re-run effect if user changes


  // Hide navbar on landing page until scrolled
  if (isLandingPage && !isScrolled) return null;

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/images/logo.jpg"
              className="h-12 w-auto"
              alt="Intellectual Oasis Fellowship Logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/cognition-lab"
              className="text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
            >
              Cognition Lab
            </Link>
            <Link
              to="/courses"
              className="text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
            >
              Courses
            </Link>
            <Link
              to="/quiz-repository"
              className="text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
            >
              Quiz Repository
            </Link>
            <Link
              to="/research"
              className="text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
            >
              Research
            </Link>
            <Link
              to="/scholarships"
              className="text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
            >
              Scholarships
            </Link>
          </div>

          {/* Auth Buttons / User Menu + Dark Mode Toggle */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <button
                  onClick={() => setIsNotificationOpen(true)}
                  className="relative text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
                  aria-label={`Notifications (${unreadCount} unread)`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.247 24.247 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                  </svg>

                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                <UserProfileIcon />
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-[rgb(130,88,18)] transition-colors duration-300"
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="bg-[rgb(130,88,18)] text-white px-6 py-2 rounded-full hover:bg-[rgb(110,68,0)] transition-colors duration-300"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <UserNotification
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        unreadCount={unreadCount} // Pass unreadCount down
      />
    </motion.nav>
  );
}

export default Navbar;